import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const QuoteForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    localidade: '',
    tipoAvaria: '',
    rgpdConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('🚀 Iniciando envio do formulário...');
    console.log('📝 Dados do formulário:', formData);

    if (!formData.rgpdConsent) {
      alert('Por favor, aceite os termos de privacidade para continuar.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Configuração do EmailJS usando variáveis de ambiente
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_plumbersnow';
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_orcamento';
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      // Prepara os dados para o EmailJS
      const templateParams = {
        from_name: formData.nome,
        from_email: formData.email,
        phone: formData.telefone,
        location: formData.localidade,
        service: formData.tipoAvaria,
        message: `Pedido de orçamento via formulário do site\n\nNome: ${formData.nome}\nTelefone: ${formData.telefone}\nEmail: ${formData.email}\nLocalidade: ${formData.localidade}\nTipo de Serviço: ${formData.tipoAvaria}`,
        to_email: 'plumbersnow.pt@gmail.com' // Email que vai receber os pedidos
      };

      console.log('📤 Enviando via EmailJS...');
      console.log('📝 Parâmetros:', templateParams);

      // Envia o email usando EmailJS
      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log('📥 Resposta recebida:', response);

      if (response.status === 200) {
        console.log('✅ Formulário enviado com sucesso!');
        setSubmitStatus('success');
        setFormData({
          nome: '',
          telefone: '',
          email: '',
          localidade: '',
          tipoAvaria: '',
          rgpdConsent: false
        });
        // Redireciona para página de obrigado após 1 segundo
        console.log('🔄 Redirecionando para /obrigado em 1 segundo...');
        setTimeout(() => {
          navigate('/obrigado');
        }, 1000);
      } else {
        console.error('❌ Erro ao enviar:', response);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('💥 Erro capturado no catch:', error);
      setSubmitStatus('error');
    } finally {
      console.log('🏁 Finalizando envio. isSubmitting = false');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Orçamento Gratuito</h2>
      <p className="text-gray-700 mb-6">Preencha o formulário e receba uma resposta rápida</p>

      <form id="orcamento-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
            Nome Completo *
          </label>
          <input
            type="text"
            id="nome"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            placeholder="O seu nome"
          />
        </div>

        <div>
          <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">
            Telefone *
          </label>
          <input
            type="tel"
            id="telefone"
            name="telefone"
            value={formData.telefone}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            placeholder="960 488 777"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            placeholder="seuemail@exemplo.com"
          />
        </div>

        <div>
          <label htmlFor="localidade" className="block text-sm font-medium text-gray-700 mb-1">
            Localidade *
          </label>
          <input
            type="text"
            id="localidade"
            name="localidade"
            value={formData.localidade}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            placeholder="Lisboa, Almada, Oeiras..."
          />
        </div>

        <div>
          <label htmlFor="tipoAvaria" className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Serviço *
          </label>
          <select
            id="tipoAvaria"
            name="tipoAvaria"
            value={formData.tipoAvaria}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">Selecione o serviço</option>
            <option value="Desentupimentos Urgentes">Desentupimentos Urgentes</option>
            <option value="Detecção e Reparação de Fugas de Água">Detecção e Reparação de Fugas de Água</option>
            <option value="Reparação de Canalização">Reparação de Canalização</option>
            <option value="Instalação de Canalização">Instalação de Canalização</option>
            <option value="Manutenção Preventiva">Manutenção Preventiva</option>
            <option value="Pinturas em Geral">Pinturas em Geral</option>
            <option value="Limpeza de Vidros de Fachada (Rapel)">Limpeza de Vidros de Fachada (Rapel)</option>
            <option value="Outro">Outro</option>
          </select>
        </div>

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="rgpdConsent"
            name="rgpdConsent"
            checked={formData.rgpdConsent}
            onChange={handleChange}
            required
            className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label htmlFor="rgpdConsent" className="text-sm text-gray-700">
            Concordo com o armazenamento dos meus dados para ser contactado pela PLUMBERS NOW.
            <span className="text-xs block mt-1 text-gray-500">
              Cumprimos RGPD. Dados usados apenas para resposta ao seu pedido.
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl mt-4 text-lg"
        >
          {isSubmitting ? 'A enviar...' : 'Pedir Orçamento Gratuito'}
        </button>

        {submitStatus === 'success' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              ✅ Pedido enviado com sucesso! Entraremos em contacto brevemente.
            </p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              ❌ Erro ao enviar. Tente novamente ou ligue 960 488 777.
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default QuoteForm;
